/*
 * P4-18 — Phase 1 device-only guest Wishlist (D-186/C-217).
 *
 * First-party, product-level, device-only. The browser stores product identity and save
 * order only; every commerce fact (price, availability, image, badges, URL) is rendered
 * from current Shopify data through the existing `product-card` snippet and the existing
 * quick-buy / product-form behaviour. This file never adds to cart and never selects a
 * variant.
 *
 * Storage key: dwc:wishlist:v1 — schema version 1:
 *   { "version": 1, "updatedAt": "<iso>", "items": [ { "productId": "<digits>", "addedAt": "<iso>" } ] }
 *
 * Ordering comparator (single, used everywhere): ascending `addedAt`, then ascending
 * `productId`, both compared as strings. `addedAt` is always written as a UTC ISO-8601
 * instant with milliseconds, so string order and chronological order are identical.
 */

(function (global) {
  'use strict';

  var STORAGE_KEY = 'dwc:wishlist:v1';
  var SCHEMA_VERSION = 1;
  var BATCH_SIZE = 12;
  var CHANGE_EVENT = 'dwc:wishlist:change';

  var PRODUCT_ID_PATTERN = /^[1-9][0-9]*$/;
  var INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

  var STATUS_OK = 'ok';
  var STATUS_UNREADABLE = 'unreadable';
  var STATUS_UNAVAILABLE = 'unavailable';

  /* ------------------------------------------------------------------ schema */

  /* The one comparator. Deterministic; product ID is the stable tie-breaker. */
  function compareItems(a, b) {
    if (a.addedAt < b.addedAt) return -1;
    if (a.addedAt > b.addedAt) return 1;
    if (a.productId < b.productId) return -1;
    if (a.productId > b.productId) return 1;
    return 0;
  }

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function sortedKeys(value) {
    return Object.keys(value).sort().join(',');
  }

  function unreadable() {
    return { status: STATUS_UNREADABLE, items: [] };
  }

  /*
   * Strict parse. Anything that is not exactly schema v1 is reported as unreadable and the
   * stored payload is left untouched — it is never repaired, rewritten or cleared.
   */
  function parseRecord(parsed) {
    if (!isPlainObject(parsed)) return unreadable();
    if (sortedKeys(parsed) !== 'items,updatedAt,version') return unreadable();
    if (parsed.version !== SCHEMA_VERSION) return unreadable();
    if (typeof parsed.updatedAt !== 'string' || !INSTANT_PATTERN.test(parsed.updatedAt)) return unreadable();
    if (!Array.isArray(parsed.items)) return unreadable();

    var items = [];

    for (var i = 0; i < parsed.items.length; i++) {
      var entry = parsed.items[i];

      if (!isPlainObject(entry)) return unreadable();
      if (sortedKeys(entry) !== 'addedAt,productId') return unreadable();
      if (typeof entry.productId !== 'string' || !PRODUCT_ID_PATTERN.test(entry.productId)) return unreadable();
      if (typeof entry.addedAt !== 'string' || !INSTANT_PATTERN.test(entry.addedAt)) return unreadable();

      items.push({ productId: entry.productId, addedAt: entry.addedAt });
    }

    items.sort(compareItems);

    /* Product-level deduplication: after sorting, the first occurrence wins. */
    var deduped = [];
    var seen = {};

    for (var j = 0; j < items.length; j++) {
      var key = 'p' + items[j].productId;

      if (Object.prototype.hasOwnProperty.call(seen, key)) continue;

      seen[key] = true;
      deduped.push(items[j]);
    }

    return { status: STATUS_OK, items: deduped };
  }

  /* Writes exactly `version`, `updatedAt` and `items`, and exactly `productId`/`addedAt`. */
  function serializeRecord(items, updatedAt) {
    var payload = { version: SCHEMA_VERSION, updatedAt: updatedAt, items: [] };

    for (var i = 0; i < items.length; i++) {
      payload.items.push({ productId: items[i].productId, addedAt: items[i].addedAt });
    }

    return JSON.stringify(payload);
  }

  function normalizeProductId(value) {
    if (typeof value === 'number' && isFinite(value) && Math.floor(value) === value && value > 0) {
      return String(value);
    }

    if (typeof value === 'string' && PRODUCT_ID_PATTERN.test(value)) return value;

    return null;
  }

  /* ----------------------------------------------------------------- storage */

  function createStore(options) {
    var storage = options.storage;
    var now = options.now;
    var committed = null;

    function resolveStorage() {
      /* Privacy modes throw on property access, not only on use. */
      try {
        if (!storage) return null;
        if (typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function') return null;
        return storage;
      } catch (error) {
        return null;
      }
    }

    function read() {
      var target = resolveStorage();

      if (!target) return { status: STATUS_UNAVAILABLE, items: [] };

      var raw;

      try {
        raw = target.getItem(STORAGE_KEY);
      } catch (error) {
        return { status: STATUS_UNAVAILABLE, items: [] };
      }

      if (raw === null || raw === undefined) return { status: STATUS_OK, items: [] };

      var parsed;

      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return unreadable();
      }

      return parseRecord(parsed);
    }

    /* Read-through: storage is authoritative, so a refresh always restores saved state. */
    function state() {
      committed = read();
      return committed;
    }

    function has(productId) {
      var id = normalizeProductId(productId);

      if (id === null) return false;

      var current = state();

      for (var i = 0; i < current.items.length; i++) {
        if (current.items[i].productId === id) return true;
      }

      return false;
    }

    function commit(current, nextItems) {
      var target = resolveStorage();

      if (!target) return { ok: false, reason: STATUS_UNAVAILABLE, changed: false };

      try {
        target.setItem(STORAGE_KEY, serializeRecord(nextItems, now()));
      } catch (error) {
        /* Quota or write failure: the last valid state stays committed, in memory and on disk. */
        committed = current;
        return { ok: false, reason: 'write', changed: false };
      }

      committed = { status: STATUS_OK, items: nextItems };
      return { ok: true, reason: null, changed: true };
    }

    function add(productId) {
      var id = normalizeProductId(productId);

      if (id === null) return { ok: false, reason: 'invalid', changed: false };

      var current = state();

      /* Never overwrite a payload we could not read, and never write when storage is out. */
      if (current.status !== STATUS_OK) return { ok: false, reason: current.status, changed: false };

      for (var i = 0; i < current.items.length; i++) {
        if (current.items[i].productId === id) return { ok: true, reason: null, changed: false };
      }

      var next = current.items.slice();
      next.push({ productId: id, addedAt: now() });
      next.sort(compareItems);

      return commit(current, next);
    }

    function remove(productId) {
      var id = normalizeProductId(productId);

      if (id === null) return { ok: false, reason: 'invalid', changed: false };

      var current = state();

      if (current.status !== STATUS_OK) return { ok: false, reason: current.status, changed: false };

      var next = [];
      var found = false;

      for (var i = 0; i < current.items.length; i++) {
        if (current.items[i].productId === id) {
          found = true;
          continue;
        }

        next.push(current.items[i]);
      }

      if (!found) return { ok: true, reason: null, changed: false };

      return commit(current, next);
    }

    /*
     * Exact compare-and-swap clear. Removes ONLY rows matching both the product and the exact
     * `addedAt` that was acknowledged, and commits once. A product removed and re-saved while
     * the merge was in flight has a different `addedAt`, so the late acknowledgement leaves the
     * new save alone. Unreadable or unavailable storage is never written.
     */
    function clearAcknowledged(entries) {
      if (!entries || entries.length === 0) return { ok: true, reason: null, changed: false, cleared: [] };

      var current = state();

      if (current.status !== STATUS_OK) return { ok: false, reason: current.status, changed: false, cleared: [] };

      var acknowledged = {};

      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];

        if (!entry || typeof entry.productId !== 'string' || typeof entry.addedAt !== 'string') continue;

        acknowledged[entry.productId + '@' + entry.addedAt] = true;
      }

      var next = [];
      var cleared = [];

      for (var j = 0; j < current.items.length; j++) {
        var item = current.items[j];

        if (Object.prototype.hasOwnProperty.call(acknowledged, item.productId + '@' + item.addedAt)) {
          cleared.push(item.productId);
          continue;
        }

        next.push(item);
      }

      /* Nothing still matches — the record moved on entirely; leave it exactly as it is. */
      if (cleared.length === 0) return { ok: true, reason: null, changed: false, cleared: [] };

      var committed = commit(current, next);

      return {
        ok: committed.ok,
        reason: committed.reason,
        changed: committed.ok,
        cleared: committed.ok ? cleared : []
      };
    }

    function ids() {
      var current = state();
      var out = [];

      for (var i = 0; i < current.items.length; i++) out.push(current.items[i].productId);

      return out;
    }

    return {
      read: read,
      state: state,
      has: has,
      add: add,
      remove: remove,
      clearAcknowledged: clearAcknowledged,
      ids: ids,
      committed: function () {
        return committed;
      }
    };
  }

  /* ---------------------------------------------------- current-Shopify render */

  function normalizeRoot(root) {
    var value = typeof root === 'string' && root ? root : '/';

    if (value.charAt(value.length - 1) !== '/') value += '/';

    return value;
  }

  function batchIds(ids, size) {
    var limit = size > 0 ? size : BATCH_SIZE;
    var batches = [];

    for (var i = 0; i < ids.length; i += limit) batches.push(ids.slice(i, i + limit));

    return batches;
  }

  /*
   * Same-origin, locale-aware Shopify section rendering — the identical mechanism the theme
   * already uses for recently viewed products. No external host, no account endpoint, no
   * Storefront token, no app proxy.
   */
  function buildSearchUrl(root, sectionId, ids) {
    var terms = [];

    for (var i = 0; i < ids.length; i++) terms.push('id:' + ids[i]);

    return normalizeRoot(root) + 'search?type=product&q=' + encodeURIComponent(terms.join(' OR ')) +
      '&section_id=' + encodeURIComponent(sectionId);
  }

  /* Keep only nodes whose product ID was actually requested, keyed for saved-order rebuild. */
  function indexRenderedItems(nodes, requestedIds) {
    var allowed = {};
    var index = {};

    for (var i = 0; i < requestedIds.length; i++) allowed['p' + requestedIds[i]] = true;

    for (var j = 0; j < nodes.length; j++) {
      var id = nodes[j].getAttribute('data-wishlist-product-id');

      if (!id || !PRODUCT_ID_PATTERN.test(id)) continue;
      if (!Object.prototype.hasOwnProperty.call(allowed, 'p' + id)) continue;
      if (Object.prototype.hasOwnProperty.call(index, 'p' + id)) continue;

      index['p' + id] = nodes[j];
    }

    return index;
  }

  /* Saved order wins; search-result order is discarded. */
  function orderBySavedIds(savedIds, index) {
    var resolved = [];
    var unresolved = [];

    for (var i = 0; i < savedIds.length; i++) {
      var key = 'p' + savedIds[i];

      if (Object.prototype.hasOwnProperty.call(index, key)) {
        resolved.push({ productId: savedIds[i], node: index[key] });
      } else {
        unresolved.push(savedIds[i]);
      }
    }

    return { resolved: resolved, unresolved: unresolved };
  }

  /* ------------------------------------------------------------------ runtime */

  function readConfig(scope) {
    var config = scope && scope.DWCWishlistConfig;

    if (!isPlainObject(config)) return null;
    if (config.enabled !== true) return null;

    return config;
  }

  function text(config, key) {
    var strings = isPlainObject(config.strings) ? config.strings : {};
    return typeof strings[key] === 'string' ? strings[key] : '';
  }

  function start(scope) {
    var config = readConfig(scope);

    /*
     * Feature-disabled contract: with `enable_wishlist` false the layout does not emit the
     * config, the CSS or this file at all. Even if it were loaded, nothing below runs — no
     * listener, no storage access, no request.
     */
    if (!config) return null;

    /* Re-entrant: a theme-editor section reload re-syncs instead of double-binding. */
    if (scope.DWCWishlist && typeof scope.DWCWishlist.reload === 'function') {
      scope.DWCWishlist.reload();
      return scope.DWCWishlist;
    }

    var doc = scope.document;
    var store = createStore({
      storage: (function () {
        try {
          return scope.localStorage;
        } catch (error) {
          return null;
        }
      })(),
      now: function () {
        return new Date().toISOString();
      }
    });

    var liveRegion = null;
    var lastAnnouncement = null;

    /* Set below, once the config has been read. Null whenever account sync is off. */
    var account = null;

    /*
     * The authoritative list for rendering.
     *
     * In account mode the account is authoritative, but independent guest-only products must
     * stay visible: a device Add kept during an outage, or anything a merge has not yet
     * acknowledged, is real saved work the shopper can see and act on. The two lists are
     * therefore presented as ONE product-unique list — account rows first, in the order the
     * server gave them, then guest-only rows in their existing local order.
     *
     * This is a read-only union for display. Nothing account-derived is copied into guest
     * storage by it, so a shared browser still cannot leak one customer's Wishlist to the next.
     */
    function activeState() {
      if (account && account.active()) {
        var accountItems = account.state().items;
        var merged = accountItems.slice();
        var seen = {};
        var i;

        for (i = 0; i < accountItems.length; i++) seen['p' + accountItems[i].productId] = true;

        var guestOnly = store.state();

        /* An unreadable or unavailable guest payload simply contributes nothing. */
        if (guestOnly.status === STATUS_OK) {
          for (i = 0; i < guestOnly.items.length; i++) {
            var candidate = guestOnly.items[i];

            if (Object.prototype.hasOwnProperty.call(seen, 'p' + candidate.productId)) continue;

            seen['p' + candidate.productId] = true;
            merged.push(candidate);
          }
        }

        return { status: STATUS_OK, items: merged, source: 'account' };
      }

      var guest = store.state();

      return { status: guest.status, items: guest.items, source: 'guest' };
    }

    /*
     * Prestige already emits one polite, atomic status region in `layout/theme.liquid`, on
     * every page. The wishlist uses that single shared destination so a save, a removal or a
     * failure is announced identically on the PDP, a collection, the home page and the
     * wishlist page — the controls are not confined to the wishlist page, so the destination
     * must not be either.
     */
    function announce(message) {
      if (!message) return;

      if (!liveRegion) {
        liveRegion = doc.getElementById('status-announcement');
      }

      if (!liveRegion) return;

      /* One polite, atomic route; identical consecutive messages are re-announced by
         clearing first, and a single action never announces twice. */
      liveRegion.textContent = '';
      liveRegion.textContent = message;
      lastAnnouncement = message;
    }

    function toggles() {
      return doc.querySelectorAll('[data-wishlist-toggle]');
    }

    /*
     * The Wishlist page's one-line disclosure of WHERE the saved list actually lives.
     *
     * Both strings are server-rendered and the device one is authoritative until proven
     * otherwise, so a cached page can never open claiming an account it does not have. The
     * account line is shown only while the adapter holds a live, confirmed session — the same
     * condition `activeState()` uses to treat the account as authoritative — so the disclosure
     * and the list it sits above can never disagree.
     *
     * With account sync off the account line is not rendered at all, so this is inert and the
     * page reads exactly as Phase 1 did.
     */
    /*
     * One device-and-account pair. Either element being absent leaves BOTH untouched, so the
     * server-rendered default stands: with account sync off the account element is never
     * rendered, and the device wording is what the shopper keeps seeing.
     */
    function swapScopePair(deviceEl, accountEl, onAccount) {
      if (!deviceEl || !accountEl) return;

      /* The theme's existing show/hide convention: the `hidden` ATTRIBUTE, not the IDL property. */
      if (onAccount) {
        accountEl.removeAttribute('hidden');
        deviceEl.setAttribute('hidden', '');
      } else {
        deviceEl.removeAttribute('hidden');
        accountEl.setAttribute('hidden', '');
      }
    }

    function syncScopeDisclosure() {
      /*
       * `active()` is a CONFIRMED session — the service has answered this page authenticated —
       * so both pairs swap on proof rather than on the server-rendered hint. A refusal or a lost
       * session clears it, and because this runs from `syncControls()` the wording follows an
       * identity change within the same page session, with no reload.
       */
      var onAccount = !!(account && account.active());

      swapScopePair(
        doc.querySelector('[data-wishlist-scope-device]'),
        doc.querySelector('[data-wishlist-scope-account]'),
        onAccount
      );

      /*
       * P11-05: the empty state carries ONE paragraph — the signed-out invitation to sign in.
       * A confirmed session hides it rather than replacing it, because the disclosure above
       * already states where the list lives and saying it twice helps nobody. Same signal, so
       * the two can never disagree; absent element, or account sync off, leaves it visible.
       */
      var emptySignedOut = doc.querySelector('[data-wishlist-empty-signed-out]');

      if (emptySignedOut) {
        if (onAccount) emptySignedOut.setAttribute('hidden', '');
        else emptySignedOut.removeAttribute('hidden');
      }
    }

    function syncControls() {
      var current = activeState();
      var saved = {};

      syncScopeDisclosure();

      for (var i = 0; i < current.items.length; i++) saved['p' + current.items[i].productId] = true;

      var controls = toggles();

      for (var j = 0; j < controls.length; j++) {
        var control = controls[j];
        var id = control.getAttribute('data-wishlist-product-id');

        if (!id) continue;

        var pressed = Object.prototype.hasOwnProperty.call(saved, 'p' + id);
        var label = pressed
          ? control.getAttribute('data-wishlist-remove-label')
          : control.getAttribute('data-wishlist-add-label');

        control.setAttribute('aria-pressed', pressed ? 'true' : 'false');

        if (label) control.setAttribute('aria-label', label);
      }
    }

    /* ------------------------------------------------ late-rendered controls */

    var observer = null;
    var observerCount = 0;

    /*
     * True only when a child-list mutation actually introduced a wishlist control. Attribute
     * mutations are never observed, so `syncControls` writing `aria-pressed` and `aria-label`
     * can never feed back into this route.
     */
    function addsControls(records) {
      if (!records) return false;

      for (var i = 0; i < records.length; i++) {
        var record = records[i];
        var added = record && record.addedNodes;

        if (!added || !added.length) continue;

        for (var j = 0; j < added.length; j++) {
          var node = added[j];

          /* Text and comment nodes carry no attributes and cannot be a control. */
          if (!node || typeof node.getAttribute !== 'function') continue;
          if (node.getAttribute('data-wishlist-toggle') !== null) return true;
          if (typeof node.querySelector === 'function' &&
            node.querySelector('[data-wishlist-toggle]')) return true;
        }
      }

      return false;
    }

    function onLateMutations(records) {
      if (!addsControls(records)) return;

      /*
       * Read-only synchronization: the stored payload is read, the new control is given its
       * correct pressed state and label, and nothing else happens — no write, no request and
       * no announcement, because the customer did not act.
       */
      syncControls();
    }

    /*
     * Registered exactly once per started runtime. Prestige fetches related products, and
     * other surfaces inject cards, after the initial paint; without this a saved product's
     * control would render with the server default and the next click would remove the item
     * the customer meant to add.
     */
    function observeLateControls() {
      if (observer) return observer;

      var Observer = scope.MutationObserver;

      if (typeof Observer !== 'function') return null;

      var target = doc.body || doc.documentElement || doc;

      if (!target) return null;

      var candidate;

      try {
        candidate = new Observer(onLateMutations);
      } catch (error) {
        return null;
      }

      if (!candidate || typeof candidate.observe !== 'function') return null;

      try {
        candidate.observe(target, { childList: true, subtree: true });
      } catch (error) {
        return null;
      }

      observerCount += 1;
      observer = candidate;

      return observer;
    }

    function notifyChange(detail) {
      syncControls();

      /* Narrowly scoped, wishlist-only event. No unrelated theme event system is touched. */
      doc.dispatchEvent(new scope.CustomEvent(CHANGE_EVENT, { bubbles: false, detail: detail }));
    }

    function messageForFailure(reason) {
      if (reason === STATUS_UNAVAILABLE) return text(config, 'unavailable');
      return text(config, 'loadError');
    }

    /*
     * In account mode the server is authoritative: controls and the page change only after a
     * confirmed result. A failure keeps the previous visible state and announces the failure —
     * an account Remove that cannot reach the server must never look as if it succeeded.
     *
     * Two cases are deliberately NOT server round-trips:
     *
     *  - a guest-only product, which the account never held, is removed locally with no account
     *    Remove request at all;
     *  - a device-originated Add that the account refuses (network, non-JSON, 401, 403, an
     *    exhausted 409, 429 or 5xx) falls back to the independent guest Wishlist. When that
     *    local save succeeds the product really is saved, so it is shown as saved immediately
     *    and announced as device-only — not as a failure. Only when the local save ALSO fails
     *    is nothing shown as saved and the load error announced.
     */
    function toggleAccount(productId) {
      var id = String(productId);
      var inAccount = account.has(id);

      if (!inAccount && store.has(id)) {
        var localRemoval = store.remove(id);

        if (!localRemoval.ok) {
          announce(messageForFailure(localRemoval.reason));
          syncControls();
          return Promise.resolve({ ok: false, reason: localRemoval.reason, changed: false });
        }

        announce(text(config, 'removed'));
        notifyChange({ productId: id, removed: true });

        return Promise.resolve({ ok: true, reason: null, changed: true, source: 'guest' });
      }

      var startedAt = account.generation();
      var pending = inAccount ? account.remove(id) : account.add(id);

      return pending.then(function (next) {
        /*
         * The generation moved while this mutation was in flight. Two very different things
         * cause that and they must not be treated alike:
         *
         *  - the SESSION ended under this very request (401/403). The shopper is a guest now, so
         *    their own device-originated Add is still kept on this device, exactly as any other
         *    outage would be handled.
         *  - any other identity change — a different customer became current, or the page was
         *    hidden and account memory dropped — means this response belongs to somebody who is
         *    no longer signed in. It is discarded: no control, no page, no announcement and no
         *    guest write. Treating it as a failure would push customer A's product into the
         *    browser while customer B is signed in.
         */
        var endedUnderThisRequest = !inAccount && account.sessionEnded() && !account.active();

        if (account.generation() !== startedAt && !endedUnderThisRequest) {
          return { ok: false, reason: 'stale', changed: false, stale: true };
        }

        if (next === null) {
          /* A Remove is never guessed at: the account state stands and the failure is spoken. */
          if (inAccount) {
            announce(text(config, 'loadError'));
            syncControls();
            return { ok: false, reason: 'account', changed: false };
          }

          var fallback = store.add(id);

          if (!fallback.ok) {
            /* Nothing was saved anywhere, so nothing may render as saved. */
            announce(text(config, 'loadError'));
            syncControls();
            return { ok: false, reason: fallback.reason, changed: false };
          }

          announce(text(config, 'deviceOnly'));
          notifyChange({ productId: id, removed: false, deviceOnly: true });

          return { ok: true, reason: 'device_only', changed: true, deviceOnly: true };
        }

        /*
         * A confirmed account Remove also drops any independent guest copy of the same product,
         * so one removal removes it once from the single product-unique list the shopper sees.
         */
        if (inAccount && store.has(id)) store.remove(id);

        announce(inAccount ? text(config, 'removed') : text(config, 'saved'));
        notifyChange({ productId: id, removed: inAccount });

        return { ok: true, reason: null, changed: true };
      });
    }

    function toggle(productId) {
      if (account && account.active()) return toggleAccount(productId);

      var result = store.has(productId) ? store.remove(productId) : store.add(productId);
      var wasRemoval = result.ok && result.changed && !store.has(productId);

      if (!result.ok) {
        /* No success is announced and no committed state changed. */
        announce(messageForFailure(result.reason));
        syncControls();
        return result;
      }

      if (result.changed) {
        announce(wasRemoval ? text(config, 'removed') : text(config, 'saved'));
      }

      notifyChange({ productId: String(productId), removed: wasRemoval });
      return result;
    }

    function onClick(event) {
      var target = event.target;
      var control = null;

      while (target && target !== doc) {
        if (target.getAttribute && target.getAttribute('data-wishlist-toggle') !== null &&
          target.getAttribute('data-wishlist-toggle') !== undefined) {
          control = target;
          break;
        }

        target = target.parentNode;
      }

      if (!control) return;

      var id = control.getAttribute('data-wishlist-product-id');

      if (!id) return;

      toggle(id);
    }

    function onStorage(event) {
      if (!event || event.key !== STORAGE_KEY) return;

      /* Another tab changed (or an external actor cleared) the key: re-read and re-render.
         The acting tab already announced, so this tab stays silent. */
      syncControls();

      if (page) page.refresh();
    }

    var pageOptions = {
      doc: doc,
      scope: scope,
      config: config,
      store: store,
      announce: announce,
      syncControls: syncControls,
      /* The wishlist page renders whichever list is authoritative right now. */
      source: activeState
    };

    var page = createPage(pageOptions);

    function reload() {
      syncControls();

      /* Idempotent: this returns the existing observer, so the re-entrant reload path can
         never register a second one. */
      observeLateControls();

      if (!page) page = createPage(pageOptions);
      if (page) page.render();
    }

    doc.addEventListener('click', onClick);
    scope.addEventListener('storage', onStorage);

    syncControls();
    observeLateControls();

    /*
     * P4-27: the account adapter exists only when the account-sync flag is on. When it is off
     * this stays null, so no account request is ever issued and Phase 1 behaviour is exact.
     */
    account = config.accountSyncEnabled === true
      ? createAccountAdapter({
        scope: scope,
        config: config,
        store: store,
        onChange: function () {
          syncControls();
          if (page) page.refresh();
        }
      })
      : null;

    /*
     * Lifecycle. `signedIn` is a SERVER-RENDERED hint that only decides whether to attempt the
     * proxy request on the INITIAL load; identity itself always comes from Shopify's
     * signature-validated proxy fields. It is deliberately not trusted afterwards: a page
     * restored from the back/forward cache, or a tab returning to the foreground, may be
     * carrying markup that was rendered before the shopper signed in, so those paths force a
     * revalidation and a 401/403 falls straight back to guest mode.
     *
     * `revalidation` holds the one in-flight attempt. Rapid lifecycle events join it instead of
     * starting another, so a burst of `pageshow` and `visibilitychange` events can never stack
     * requests or drive a loop.
     */
    var revalidation = null;
    var revalidationGeneration = -1;
    var revalidationForced = false;

    /*
     * The shared attempt, but only while it is still current. `forget()`, a 401/403 or a switch
     * to another customer bumps the adapter generation, which guarantees the response that
     * attempt is waiting for will be discarded. Joining it would mean waiting for a result that
     * can never be used, so a superseded attempt is dropped and the caller starts a fresh one.
     */
    function pendingRevalidation() {
      if (revalidation === null) return null;
      if (account.generation() !== revalidationGeneration) return null;

      return revalidation;
    }

    function initAccount(options) {
      var opts = options || {};

      if (!account) return Promise.resolve(null);
      if (opts.force !== true && config.signedIn === false) return Promise.resolve(null);

      var shared = pendingRevalidation();

      if (shared) return shared;

      var startedAt = account.generation();

      /* Only the attempt that still owns the slot may clear it. */
      function settle(result) {
        if (revalidationGeneration === startedAt) revalidation = null;
        return result;
      }

      revalidationGeneration = startedAt;
      revalidationForced = opts.force === true;

      revalidation = account.load().then(function (state) {
        if (state === null) {
          /* Refused or lost: repaint so nothing account-derived is left on screen. */
          syncControls();
          if (page) page.refresh();
          return null;
        }

        /* A successful read attempts the one-time guest merge when there is anything to send. */
        return account.merge().then(function () {
          syncControls();
          if (page) page.refresh();
          return account.state();
        });
      }).then(settle, function (error) {
        settle(null);
        throw error;
      });

      return revalidation;
    }

    /*
     * Identity can be lost or swapped without a full navigation — a sign-out in another tab, a
     * back/forward-cache restore, or a tab left in the background. Any of those drops
     * account-derived memory FIRST, so customer A can never remain on screen while customer B
     * or a sign-out is still being checked, and only then revalidates.
     */
    /*
     * MAY this page plausibly hold a customer session at all?
     *
     * The three answers, most authoritative first:
     *
     *   1. a live confirmed session — the service has already answered this page authenticated,
     *      so identity exists whatever the server hint said;
     *   2. a session the service has REFUSED (401/403) — proven absent, and the server hint is
     *      proven stale, so we stop asking. A shopper who signs out in another tab therefore costs
     *      exactly one refused request, not one per tab focus;
     *   3. otherwise the server-rendered hint, which is exactly what the initial load trusts.
     *
     * This never widens access. It only declines to send requests that cannot succeed: every
     * request that would have been authenticated is still sent, and a refusal still falls straight
     * back to guest mode.
     */
    function mayHaveAccountSession() {
      if (!account) return false;
      if (account.active()) return true;
      if (account.sessionEnded()) return false;

      return config.signedIn !== false;
    }

    /*
     * `requireSession` separates the two lifecycle triggers, which are NOT equally costly.
     *
     * `pageshow[persisted]` is a genuine back/forward-cache restore: the markup really was
     * rendered at some earlier moment and really may be stale, and it fires once per restore. It
     * revalidates unconditionally, so cache recovery is preserved exactly as before.
     *
     * `visibilitychange` is different in kind. Nothing was re-rendered — the tab has been alive
     * since it loaded — and it fires on EVERY tab switch, for every visitor, for the whole
     * session. Forcing there bypassed the `signedIn === false` gate in `initAccount`, so an
     * ordinary signed-out shopper issued an account request per tab focus; Shopify signs those
     * with an empty `logged_in_customer_id` and the service refuses them 403 in ~2 ms. That
     * unbounded stream is the production defect, and the initial-load gate was powerless against
     * it because the first tab focus defeated it.
     *
     * Gating only this path keeps the security-critical direction intact. A page rendered
     * signed-in still revalidates on every focus, so a sign-out in another tab is still detected
     * and account rows still leave the screen at once. What is given up is the reverse and far
     * less costly case: a shopper who signs in elsewhere and returns to an untouched signed-out
     * tab now picks the account up on their next navigation or bfcache restore rather than on
     * focus.
     */
    function revalidateIdentity(options) {
      var opts = options || {};

      /*
       * Checked BEFORE `forget()`, because forgetting clears the very live-session evidence this
       * decision rests on.
       */
      if (opts.requireSession === true && !mayHaveAccountSession()) return Promise.resolve(null);

      /*
       * Another lifecycle revalidation is already running against the current generation, and it
       * already cleared account memory: join it rather than restarting, so a burst of events
       * issues one request.
       *
       * A read that was NOT started by a lifecycle event — the initial load — is deliberately
       * never joined. A lifecycle event is precisely the signal that identity may have changed
       * since that request was sent, so its answer cannot be trusted and it is superseded here.
       */
      if (revalidationForced) {
        var shared = pendingRevalidation();

        if (shared) return shared;
      }

      account.forget();
      syncControls();
      if (page) page.refresh();

      return initAccount({ force: true });
    }

    if (account) {
      /* A bfcache restore may be a different session entirely: drop and revalidate. */
      scope.addEventListener('pageshow', function (event) {
        if (event && event.persisted) revalidateIdentity();
      });

      scope.addEventListener('pagehide', function () {
        account.forget();
      });

      /*
       * A restored tab is the same risk as a bfcache restore: the shopper may have signed out,
       * or signed in as somebody else, in another tab while this one was hidden.
       */
      doc.addEventListener('visibilitychange', function () {
        if (doc.visibilityState === 'hidden') return;
        revalidateIdentity({ requireSession: true });
      });
    }

    if (page) page.render();

    /* Automatic: a signed-in shopper reaches account mode without any extra call site. */
    initAccount();

    var api = {
      STORAGE_KEY: STORAGE_KEY,
      SCHEMA_VERSION: SCHEMA_VERSION,
      BATCH_SIZE: BATCH_SIZE,
      CHANGE_EVENT: CHANGE_EVENT,
      compareItems: compareItems,
      parseRecord: parseRecord,
      serializeRecord: serializeRecord,
      normalizeProductId: normalizeProductId,
      batchIds: batchIds,
      buildSearchUrl: buildSearchUrl,
      indexRenderedItems: indexRenderedItems,
      orderBySavedIds: orderBySavedIds,
      store: store,
      toggle: toggle,
      syncControls: syncControls,
      reload: reload,
      page: page,
      observeLateControls: observeLateControls,
      observerCount: function () {
        return observerCount;
      },
      /* Null unless `account_wishlist_sync_enabled` is true. Never persisted. */
      account: account,
      initAccount: initAccount,
      /* Whether a forced lifecycle revalidation may be attempted at all. */
      mayHaveAccountSession: mayHaveAccountSession,
      revalidateIdentity: revalidateIdentity,
      syncScopeDisclosure: syncScopeDisclosure,
      /* True while the single shared, still-current identity revalidation is in flight. */
      revalidating: function () {
        return pendingRevalidation() !== null;
      },
      lastAnnouncement: function () {
        return lastAnnouncement;
      }
    };

    scope.DWCWishlist = api;
    return api;
  }

  /* ------------------------------------------------------- account sync (P4-27) */

  /*
   * Canonical guest snapshot -> deterministic cryptographic idempotency key.
   *
   * The key must be stable for the same snapshot so a lost response can be retried safely, and
   * must change when any normalized field changes so a different snapshot can never replay an
   * older merge. It is derived ONLY from the snapshot: no customer identity takes part, so the
   * same device snapshot merged into two different accounts still hashes the same.
   *
   * SHA-256 through Web Crypto — no third-party dependency. The digest is 64 hex characters,
   * giving a 70-character key, comfortably inside the service's 16..128 contract.
   */
  function canonicalSnapshot(items) {
    var parts = [];

    for (var i = 0; i < items.length; i++) {
      parts.push(items[i].productId + '@' + items[i].addedAt);
    }

    return 'dwc:wishlist:v1|' + parts.join('|');
  }

  function sha256Hex(scope, text) {
    var subtle = scope.crypto && scope.crypto.subtle;

    if (!subtle || typeof subtle.digest !== 'function' || typeof scope.TextEncoder !== 'function') {
      return Promise.resolve(null);
    }

    return subtle.digest('SHA-256', new scope.TextEncoder().encode(text)).then(function (buffer) {
      var bytes = new Uint8Array(buffer);
      var hex = '';

      for (var i = 0; i < bytes.length; i++) {
        hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
      }

      return hex;
    }, function () {
      return null;
    });
  }

  function snapshotKey(scope, items) {
    return sha256Hex(scope, canonicalSnapshot(items)).then(function (hex) {
      return hex === null ? null : 'guest-' + hex;
    });
  }

  function operationKey(scope, operation, productId, expectedRevision) {
    return sha256Hex(scope, operation + '|' + productId + '|r' + expectedRevision).then(function (hex) {
      return hex === null ? null : operation + '-' + hex;
    });
  }

  /*
   * The account adapter is constructed ONLY when `account_wishlist_sync_enabled` is true. With
   * the flag false nothing below is created, so the runtime makes no account request, holds no
   * account state and behaves exactly as Phase 1 did.
   *
   * Everything the account layer knows lives in this closure: state, tokens and pending work
   * are memory-only and die with the page. Nothing account-derived is ever written to any
   * browser persistence surface, so a shared browser cannot leak one customer's Wishlist to
   * the next.
   *
   * Race safety is a generation counter. Every request captures the generation it started in;
   * `invalidate()` (sign-out, identity change, 401/403, bfcache restore) increments it and
   * aborts what it can. A response whose generation is stale is discarded without touching
   * state, so a slow customer-A read can never repopulate after customer B has begun.
   */
  function createAccountAdapter(options) {
    var scope = options.scope;
    var config = options.config;
    var store = options.store;
    var onChange = typeof options.onChange === 'function' ? options.onChange : function () {};

    var endpoint = typeof config.accountEndpoint === 'string' && config.accountEndpoint
      ? config.accountEndpoint
      : normalizeRoot(config.rootUrl) + 'apps/wishlist/v1/wishlist';

    /* --- memory-only state --- */
    var generation = 0;
    var identity = null;
    var state = null;
    var tokens = null;
    var lastError = null;
    var mergeAttempted = false;
    var controllers = [];

    /*
     * True when the SESSION itself ended under one of our own requests (401/403), as opposed to
     * a different customer becoming current. The two look identical to a generation check but
     * mean opposite things for a device-originated Add, so they are tracked apart.
     */
    var sessionEnded = false;

    function abortInflight() {
      for (var i = 0; i < controllers.length; i++) {
        try {
          controllers[i].abort();
        } catch (error) {
          /* Aborting is best effort; a runtime without AbortController still stays safe
             because the generation check below discards the late response anyway. */
        }
      }

      controllers = [];
    }

    /* Identity loss or change. Never touches guest storage. */
    function invalidate() {
      var hadState = state !== null;

      generation += 1;
      abortInflight();
      identity = null;
      state = null;
      tokens = null;
      mergeAttempted = false;

      /*
       * Losing the session must be VISIBLE at once. Dropping the account rows without
       * repainting would leave customer A's Wishlist on screen after a 401, a sign-out or a
       * switch to customer B. This only re-renders from whatever is authoritative now — it
       * issues no request, so it cannot loop.
       */
      if (hadState) onChange();
    }

    function isStale(gen) {
      return gen !== generation;
    }

    function request(path, init, gen) {
      var options = init || {};
      var controller = null;

      if (typeof scope.AbortController === 'function') {
        controller = new scope.AbortController();
        controllers.push(controller);
        options.signal = controller.signal;
      }

      /*
       * A settled request is dropped from the in-flight collection. Without this the array grows
       * for the life of the page and every later `invalidate()` re-aborts controllers whose
       * requests finished long ago.
       */
      function settled(result) {
        if (controller !== null) {
          var index = controllers.indexOf(controller);

          if (index !== -1) controllers.splice(index, 1);
        }

        return result;
      }

      return scope.fetch(path ? endpoint + path : endpoint, options).then(function (response) {
        if (isStale(gen)) return { stale: true, ok: false, status: 0 };

        /* Identity loss is authoritative and immediate. */
        if (response.status === 401 || response.status === 403) {
          sessionEnded = true;
          invalidate();
          return { ok: false, status: response.status };
        }

        if (!response || typeof response.json !== 'function') {
          return { ok: false, status: response ? response.status : 0 };
        }

        return response.json().then(function (body) {
          if (isStale(gen)) return { stale: true, ok: false, status: 0 };
          return { ok: response.ok, status: response.status, body: body };
        }, function () {
          return { ok: false, status: response.status };
        });
      }, function () {
        /* Network failure or abort: the guest snapshot is untouched and nothing is claimed. */
        return { ok: false, status: 0, stale: isStale(gen) };
      }).then(settled);
    }

    function applyState(body) {
      state = { revision: body.revision, items: (body.items || []).slice() };
      lastError = null;
      /* A confirmed authenticated answer means the session is live again. */
      sessionEnded = false;
      onChange();
      return state;
    }

    /* Authenticated read. Also mints the mutation tokens the service requires. */
    function load() {
      var gen = generation;

      return request('', { method: 'GET', credentials: 'same-origin' }, gen).then(function (result) {
        if (result.stale || isStale(gen)) return null;

        if (!result.ok || !result.body) {
          lastError = result.status;
          return null;
        }

        /* A different signed customer means the browser switched accounts. */
        if (identity !== null && result.body.customerId !== identity) {
          invalidate();
          gen = generation;
        }

        identity = result.body.customerId || null;
        tokens = { mutate: result.body.mutationToken, merge: result.body.mergeToken };

        return applyState(result.body);
      });
    }

    function merge() {
      var gen = generation;
      var current = store.state();

      /* Never merge from a payload we could not read, and never without a live session. */
      if (current.status !== STATUS_OK || !tokens || !state) return Promise.resolve(null);

      var snapshot = current.items.slice();

      if (snapshot.length === 0) {
        mergeAttempted = true;
        return Promise.resolve({ accepted: [] });
      }

      return snapshotKey(scope, snapshot).then(function (key) {
        /* Deriving the digest is asynchronous: the session may have ended meanwhile. */
        if (isStale(gen) || !state || !tokens) return null;

        if (key === null) {
          /* No Web Crypto: refuse rather than send an unsafe key. */
          lastError = 'unsupported';
          return null;
        }

        return sendMerge(snapshot, key, state.revision, gen, false);
      });
    }

    function sendMerge(snapshot, key, expectedRevision, gen, isRetry) {
      return request('/merge', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mutationToken: tokens.merge,
          idempotencyKey: isRetry ? key + '-r1' : key,
          expectedRevision: expectedRevision,
          items: snapshot
        })
      }, gen).then(function (result) {
        if (result.stale || isStale(gen)) return null;

        /* One safe refetch and retry on a revision conflict, with a distinct key. */
        if (result.status === 409 && !isRetry && result.body && typeof result.body.revision === 'number') {
          state = { revision: result.body.revision, items: (result.body.items || []).slice() };
          return sendMerge(snapshot, key, result.body.revision, gen, true);
        }

        if (!result.ok || !result.body) {
          lastError = result.status;
          return null;
        }

        mergeAttempted = true;
        applyState(result.body);

        /*
         * Compare-and-swap on the guest side: clear ONLY the items the backend acknowledged.
         * Anything added on the device while the merge was in flight is not in that set and
         * therefore survives for the next merge.
         */
        var accepted = result.body.accepted || [];

        /* One compare-and-swap commit against the exact acknowledged snapshot entries. */
        var cleared = store.clearAcknowledged(accepted);

        return { accepted: accepted, cleared: cleared.cleared };
      });
    }

    /* Account Add / Remove. State and controls change only after a confirmed server result. */
    function mutate(operation, productId) {
      var gen = generation;

      if (!tokens || !state) return Promise.resolve(null);

      return operationKey(scope, operation, productId, state.revision).then(function (key) {
        if (isStale(gen) || !state || !tokens) return null;

        if (key === null) {
          lastError = 'unsupported';
          return null;
        }

        return sendMutation(operation, productId, key, state.revision, gen, false);
      });
    }

    function sendMutation(operation, productId, key, expectedRevision, gen, isRetry) {
      return request('/' + operation, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mutationToken: tokens.mutate,
          idempotencyKey: isRetry ? key + '-r1' : key,
          expectedRevision: expectedRevision,
          productId: String(productId)
        })
      }, gen).then(function (result) {
        if (result.stale || isStale(gen)) return null;

        if (result.status === 409 && !isRetry && result.body && typeof result.body.revision === 'number') {
          /* Refetch is already in the conflict body; retry exactly once. */
          state = { revision: result.body.revision, items: (result.body.items || []).slice() };
          return sendMutation(operation, productId, key, result.body.revision, gen, true);
        }

        if (!result.ok || !result.body) {
          lastError = result.status;
          return null;
        }

        return applyState(result.body);
      });
    }

    function has(productId) {
      if (!state) return false;

      for (var i = 0; i < state.items.length; i++) {
        if (state.items[i].productId === String(productId)) return true;
      }

      return false;
    }

    return {
      load: load,
      merge: merge,
      add: function (productId) { return mutate('add', productId); },
      remove: function (productId) { return mutate('remove', productId); },
      forget: invalidate,
      has: has,
      active: function () { return state !== null; },
      generation: function () { return generation; },
      /* Controllers for requests that are genuinely still running, and nothing else. */
      inFlight: function () { return controllers.length; },
      /* True when the session itself ended, not merely that identity moved on. */
      sessionEnded: function () { return sessionEnded; },
      state: function () { return state ? { revision: state.revision, items: state.items.slice() } : null; },
      identity: function () { return identity; },
      tokens: function () { return tokens; },
      lastError: function () { return lastError; },
      mergeAttempted: function () { return mergeAttempted; },
      snapshotKey: function (items) { return snapshotKey(scope, items); },
      endpoint: endpoint
    };
  }

  /* -------------------------------------------------------------- wishlist page */

  function createPage(options) {
    var doc = options.doc;
    var scope = options.scope;
    var config = options.config;
    var store = options.store;
    var announce = options.announce;

    var root = doc.querySelector('[data-wishlist-page]');

    if (!root) return null;

    var list = root.querySelector('[data-wishlist-list]');
    var empty = root.querySelector('[data-wishlist-empty]');
    var failure = root.querySelector('[data-wishlist-failure]');
    var unsupported = root.querySelector('[data-wishlist-unsupported]');
    var sectionId = root.getAttribute('data-wishlist-section-id') || '';
    var batchSize = parseInt(root.getAttribute('data-wishlist-batch-size'), 10) || BATCH_SIZE;
    /*
     * Render coordination. `running` is the promise for the serialized render loop and
     * `dirty` records that storage changed after the current render took its snapshot, so an
     * in-flight render can never be the final painted state.
     */
    var running = null;
    var dirty = false;
    var activeSignature = null;

    function show(node, visible) {
      if (!node) return;
      if (visible) node.removeAttribute('hidden');
      else node.setAttribute('hidden', '');
    }

    function rootUrl() {
      var shopify = scope.Shopify;

      if (shopify && shopify.routes && typeof shopify.routes.root === 'string') return shopify.routes.root;

      return typeof config.rootUrl === 'string' ? config.rootUrl : '/';
    }

    function fallbackEntry(productId) {
      var item = doc.createElement('li');

      item.className = 'wishlist__item wishlist__item--unresolved';
      item.setAttribute('data-wishlist-item', '');
      item.setAttribute('data-wishlist-product-id', productId);

      var note = doc.createElement('p');
      note.className = 'wishlist__unresolved-note';
      note.textContent = text(config, 'unresolvedItem');

      var button = doc.createElement('button');
      button.setAttribute('type', 'button');
      button.className = 'wishlist-button wishlist-button--inline button button--outline button--subdued';
      button.setAttribute('data-wishlist-toggle', '');
      button.setAttribute('data-wishlist-product-id', productId);
      button.setAttribute('data-wishlist-add-label', text(config, 'removeUnresolvedItem'));
      button.setAttribute('data-wishlist-remove-label', text(config, 'removeUnresolvedItem'));
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', text(config, 'removeUnresolvedItem'));
      button.textContent = text(config, 'removeUnresolvedItem');

      item.appendChild(note);
      item.appendChild(button);

      return item;
    }

    function fetchBatch(ids) {
      var url = buildSearchUrl(rootUrl(), sectionId, ids);

      return scope.fetch(url, { priority: 'low' }).then(function (response) {
        if (!response || response.ok === false) throw new Error('wishlist:render');
        return response.text();
      }).then(function (html) {
        var parsed = new scope.DOMParser().parseFromString(html, 'text/html');
        var nodes = parsed.querySelectorAll('[data-wishlist-item][data-wishlist-product-id]');

        return { ok: true, index: indexRenderedItems(nodes, ids) };
      }).catch(function () {
        /* A failed batch never deletes or rewrites saved IDs. */
        return { ok: false, index: {} };
      });
    }

    /* Next item first, previous item second, focusable empty-state heading last. */
    function focusAfterRemoval(removedId) {
      var items = list ? list.querySelectorAll('[data-wishlist-item]') : [];
      var position = -1;
      var target = null;

      for (var i = 0; i < items.length; i++) {
        if (items[i].getAttribute('data-wishlist-product-id') === removedId) {
          position = i;
          break;
        }
      }

      if (position !== -1 && position + 1 < items.length) target = items[position + 1];
      else if (position > 0) target = items[position - 1];

      if (target) {
        var control = target.querySelector('[data-wishlist-toggle]');

        if (control && typeof control.focus === 'function') {
          control.focus();
          return 'item';
        }
      }

      var heading = root.querySelector('[data-wishlist-empty-heading]');

      if (heading && typeof heading.focus === 'function') {
        heading.focus();
        return 'empty-heading';
      }

      return 'none';
    }

    function paint(entries, failed) {
      if (!list) return;

      while (list.firstChild) list.removeChild(list.firstChild);

      for (var i = 0; i < entries.length; i++) list.appendChild(entries[i]);

      list.setAttribute('aria-busy', 'false');

      /*
       * Failure is exclusive. A page that could not read the stored payload, or could not
       * render anything, must not simultaneously tell the customer their wishlist is empty —
       * those are contradictory claims and the empty one is the wrong reassurance.
       */
      show(failure, failed);
      show(empty, !failed && entries.length === 0);
    }

    function failureVisible() {
      return !!(failure && !failure.hasAttribute('hidden'));
    }

    /*
     * Signature of the authoritative stored state. Two renders of the same signature would
     * paint the same thing, so it is what decides whether an in-flight render is stale.
     */
    function storageSignature(state) {
      if (state.status !== STATUS_OK) return state.status;

      var ids = [];

      for (var i = 0; i < state.items.length; i++) ids.push(state.items[i].productId);

      return 'ok:' + ids.join(',');
    }

    /* A single pass: snapshot the saved IDs, render them, paint. Never called concurrently. */
    function renderOnce() {
      var current = typeof options.source === 'function' ? options.source() : store.state();

      activeSignature = storageSignature(current);

      if (current.status === STATUS_UNAVAILABLE) {
        show(unsupported, true);
        show(empty, false);
        show(failure, false);

        if (list) list.setAttribute('aria-busy', 'false');

        return Promise.resolve({ status: STATUS_UNAVAILABLE });
      }

      show(unsupported, false);

      if (current.status === STATUS_UNREADABLE) {
        paint([], true);
        return Promise.resolve({ status: STATUS_UNREADABLE });
      }

      var savedIds = [];

      for (var i = 0; i < current.items.length; i++) savedIds.push(current.items[i].productId);

      if (savedIds.length === 0) {
        paint([], false);
        return Promise.resolve({ status: 'empty', order: [] });
      }

      if (list) list.setAttribute('aria-busy', 'true');

      show(empty, false);
      show(failure, false);

      var batches = batchIds(savedIds, batchSize);
      var index = {};
      var failed = false;

      var chain = Promise.resolve();

      batches.forEach(function (batch) {
        chain = chain.then(function () {
          return fetchBatch(batch).then(function (result) {
            if (!result.ok) failed = true;

            /* Preserve every valid item even when one batch fails. */
            for (var key in result.index) {
              if (Object.prototype.hasOwnProperty.call(result.index, key)) index[key] = result.index[key];
            }
          });
        });
      });

      return chain.then(function () {
        var ordered = orderBySavedIds(savedIds, index);
        var resolvedById = {};
        var entries = [];
        var order = [];

        for (var r = 0; r < ordered.resolved.length; r++) {
          resolvedById['p' + ordered.resolved[r].productId] = ordered.resolved[r].node;
        }

        for (var j = 0; j < savedIds.length; j++) {
          var id = savedIds[j];
          var node = resolvedById['p' + id];

          /* The server already rendered each card inside its own list item; it is adopted
             as-is so no wrapper element and no client-side markup is invented. */
          entries.push(node || fallbackEntry(id));
          order.push(id);
        }

        paint(entries, failed);
        options.syncControls();

        return {
          status: failed ? 'partial' : 'ok',
          order: order,
          unresolved: ordered.unresolved,
          failed: failed
        };
      });
    }

    /*
     * Serialized render loop. Only one Shopify render runs at a time. Any refresh raised
     * while one is in flight sets `dirty`, and the loop then renders again from the newest
     * stored state, so an obsolete snapshot can never remain as the completed page state.
     * Any number of invalidations during one render coalesce into a single follow-up pass.
     */
    function drain(lastResult) {
      if (!dirty) return Promise.resolve(lastResult);

      dirty = false;

      return renderOnce().then(function (result) {
        return drain(result);
      });
    }

    /*
     * Returns a promise that settles only once the newest required render has completed,
     * and clears the running state after either success or failure.
     */
    function schedule() {
      if (running) {
        /*
         * Join the render already in flight, and mark it stale only when the stored state has
         * actually moved on since it took its snapshot. Any number of such invalidations
         * coalesce into a single follow-up pass.
         */
          if (storageSignature(typeof options.source === 'function' ? options.source() : store.state()) !== activeSignature) dirty = true;

        return running;
      }

      dirty = true;

      running = drain(null).then(function (result) {
        running = null;
        return result;
      }, function (error) {
        running = null;
        throw error;
      });

      return running;
    }

    function isRendering() {
      return running !== null;
    }

    function render() {
      return schedule();
    }

    function refresh() {
      return schedule();
    }

    function onChange(event) {
      var detail = event && event.detail ? event.detail : {};

      if (!list) return;

      if (detail.removed && detail.productId) {
        var item = list.querySelector('[data-wishlist-item][data-wishlist-product-id="' + detail.productId + '"]');

        if (item) {
          var moved = focusAfterRemoval(detail.productId);

          if (item.parentNode) item.parentNode.removeChild(item);

          var remaining = list.querySelectorAll('[data-wishlist-item]');

          /* Same exclusivity as `paint`: never claim empty while failure is on screen. */
          show(empty, !failureVisible() && remaining.length === 0);

          if (remaining.length === 0 && moved !== 'empty-heading') {
            var heading = root.querySelector('[data-wishlist-empty-heading]');
            if (heading && typeof heading.focus === 'function') heading.focus();
          }

          /*
           * The targeted removal keeps focus predictable on a settled list. If a render is
           * still in flight it holds an older snapshot, so mark the loop dirty and let it
           * repaint from the newest stored state once it drains.
           */
          if (isRendering()) schedule();

          return;
        }
      }

      refresh();
    }

    function onRetry(event) {
      var target = event.target;

      while (target && target !== doc) {
        if (target.getAttribute && target.getAttribute('data-wishlist-retry') !== null &&
          target.getAttribute('data-wishlist-retry') !== undefined) {
          refresh();
          return;
        }

        target = target.parentNode;
      }
    }

    doc.addEventListener(CHANGE_EVENT, onChange);
    doc.addEventListener('click', onRetry);

    return {
      render: render,
      refresh: refresh,
      focusAfterRemoval: focusAfterRemoval,
      element: root
    };
  }

  /* ------------------------------------------------------------------- exports */

  var moduleApi = {
    STORAGE_KEY: STORAGE_KEY,
    SCHEMA_VERSION: SCHEMA_VERSION,
    BATCH_SIZE: BATCH_SIZE,
    CHANGE_EVENT: CHANGE_EVENT,
    compareItems: compareItems,
    parseRecord: parseRecord,
    serializeRecord: serializeRecord,
    normalizeProductId: normalizeProductId,
    createStore: createStore,
    batchIds: batchIds,
    buildSearchUrl: buildSearchUrl,
    indexRenderedItems: indexRenderedItems,
    orderBySavedIds: orderBySavedIds,
    start: start
  };

  global.DWCWishlistModule = moduleApi;

  /*
   * Nothing is bound, read or requested unless the feature is enabled. With
   * `enable_wishlist` false the layout never emits this file at all; if it were loaded
   * anyway, the guard below still registers no listener and touches no storage.
   */
  if (global.document && readConfig(global)) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () {
        start(global);
      });
    } else {
      start(global);
    }

    /* Shopify theme-editor section reloads re-run the same entry point. */
    global.document.addEventListener('shopify:section:load', function () {
      start(global);
    });
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
